import { v4 as uuidv4 } from 'uuid'
import { storeToRefs } from 'pinia'
import { useVoucher, voucherErrorParser, type Voucher } from '@nsftx/seven-client-retail-vue-sgp'
import { NXGDSNotificationVariants } from '@nsftx/seven-nxg-design-system'
import { useNotificationsStore, useSpinnerStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useDeviceManagementStore, useWorkingTimeStore } from '@nsftx/seven-client-retail-vue-dm'
import printService from '../print/services/printService'
import i18n from '@/plugins/translationsPlugin'
import { useSlipCheckStore } from '@/stores/slip-check/slipCheckStore'
import { useVoucherPOSStore } from '@/stores/vouchers/voucherPOSStore'

const LOG_PREFIX = '[voucherPOSService]'
const VOUCHER_CHECK_ERROR_NOTIFICATION_ID = 'voucher_check_notification_error'
const { t } = i18n.global

const ensureWorkingHoursValid = (): boolean => {
  const { isWorkingTimeValid } = useWorkingTimeStore()
  const { isValid, nextValidTime } = isWorkingTimeValid()
  const { showNotification } = useNotificationsStore()

  if (isValid) {
    return true
  }

  showNotification({
    variant: NXGDSNotificationVariants.warning,
    text: [
      t('working_hours_off', {
        datetime: nextValidTime || t('common_unknown'),
      }),
    ],
  })

  return false
}

const printVoucher = async (voucherData: Voucher, context: string) => {
  const { showNotification } = useNotificationsStore()

  try {
    const printOptions = {
      meta: {
        requestId: uuidv4(),
        productDisplayId: 'Voucher',
      },
      model: voucherData,
    }

    await printService.getPrintService().performPrint('voucher', context, printOptions)

    showNotification({
      variant: NXGDSNotificationVariants.success,
      text: [t('voucher_print_success')],
    })
  } catch (err) {
    showNotification({
      variant: NXGDSNotificationVariants.error,
      text: [t('voucher_print_failed')],
    })
    logService.error(`${LOG_PREFIX} Failed to print voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'SS_VOUCHER_PRINT_ERROR',
    })
  }
}

const checkAndShowVoucherDetails = async (barcode: string) => {
  const spinnerStore = useSpinnerStore()
  const notificationsStore = useNotificationsStore()
  const { appData } = storeToRefs(useDeviceManagementStore())
  const slipCheckStore = useSlipCheckStore()
  const voucherPOSStore = useVoucherPOSStore()
  const { fetchVoucher, errorDetails } = useVoucher()

  notificationsStore.closeNotificationById(VOUCHER_CHECK_ERROR_NOTIFICATION_ID)

  try {
    spinnerStore.showSpinner()

    await fetchVoucher(appData.value!.betshop.companyUuid, barcode)

    slipCheckStore.closeSlipCheckModal()
    voucherPOSStore.showVoucherDetailsModal()
  } catch {
    logService.warn(`${LOG_PREFIX} Failed to check scanned voucher code`, {
      voucher_code: barcode,
      code: 'SS_VOUCHER_CHECK_SCAN_ERROR',
      upstream_message: errorDetails.value?.message,
      upstream_code: errorDetails.value?.code,
    })
    notificationsStore.showNotification({
      id: VOUCHER_CHECK_ERROR_NOTIFICATION_ID,
      text: [t('voucher_check_error')],
      variant: NXGDSNotificationVariants.error,
    })
  } finally {
    spinnerStore.closeSpinner()
  }
}

export { ensureWorkingHoursValid, printVoucher, checkAndShowVoucherDetails }
